import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Checkbox,
  Divider,
} from "@mantine/core";
import { notifyError } from "../../utils/notify";
import { IconBan, IconAlertTriangle, IconCheck } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import {
  PackageReasonCategory,
  REVOKE_REASON_CATEGORIES,
} from "../../types/packages";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import ReasonFields, { isReasonValid } from "./ReasonFields";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface VoidGiftCardModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  giftCardId: string;
  /** The card code, shown in the confirmation copy. */
  code: string;
  /** Remaining balance that will be written off. */
  balance: number;
  onSuccess: () => void | Promise<void>;
}

/**
 * Void a gift card — the destructive, irreversible analog of
 * `RevokePackageModal`. Voiding writes off the remaining balance (it is NOT a
 * refund). Requires an explicit confirmation checkbox plus a reason + note
 * (drawn from the narrower revoke reason subset, since goodwill/comp make no
 * sense when forfeiting value), and records a `void` ledger entry.
 */
export default function VoidGiftCardModal({
  opened,
  onClose,
  salonId,
  giftCardId,
  code,
  balance,
  onSuccess,
}: VoidGiftCardModalProps) {
  const { isMobile } = useResponsive();
  const { voidGiftCard } = useApp();

  const [confirmed, setConfirmed] = useState(false);
  const [category, setCategory] = useState<PackageReasonCategory | null>(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setConfirmed(false);
      setCategory(null);
      setNote("");
      setError(null);
      setIsSubmitting(false);
    }
  }, [opened]);

  const canSubmit = confirmed && isReasonValid(category, note) && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || category === null) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await voidGiftCard(salonId, giftCardId, {
        reason_category: category,
        note: note.trim(),
      });
      notifyError(`${code} has been voided.`, {
        title: "Gift card voided",
        icon: <IconCheck size={16} />,
      });
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in void success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error voiding gift card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to void the gift card. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Void Gift Card</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red">
            {error}
          </Alert>
        )}

        <Text size="sm">This writes off the remaining balance; no refund.</Text>

        <ReasonFields
          category={category}
          note={note}
          onCategoryChange={setCategory}
          onNoteChange={setNote}
          disabled={isSubmitting}
          categories={REVOKE_REASON_CATEGORIES}
        />

        <Checkbox
          color="red"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.currentTarget.checked)}
          disabled={isSubmitting}
          label={`I understand the remaining ${formatCurrency(
            balance,
          )} on ${code} will be written off and this cannot be undone.`}
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconBan size={18} />}
          >
            Void Gift Card
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
